"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
const testimonials = [{
  id: 1,
  quote: "Legacy\xA0has completely transformed our regional supply chain. Their ability to handle bulk materials with precision across the DFW metroplex is unmatched.\"\"",
  name: "Sarah Jenkins",
  role: "Logistics Director",
  company: "Texas Stone & Supply",
  avatarImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/avatar_sarah_jenkins.png",
  companyLogo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/logo_texas_stone.png"
}, {
  id: 2,
  quote: "\"When we need heavy loads moved to the Southeast region, they are our first call. The dual-market capability with semi-trucks gives us incredible flexibility.\"",
  name: "Marcus Thorne",
  role: "Operations Manager",
  company: "Thorne Lumber Co.",
  avatarImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/avatar_marcus_thorne.png",
  companyLogo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/logo_thorne_lumber.png"
}, {
  id: 3,
  quote: "Reliability is everything in raw materials. Their dedicated dispatch team ensures our shipments arrive on time, every time, keeping our projects on schedule.",
  name: "Elena Rodriguez",
  role: "Procurement VP",
  company: "BuildCorp Materials",
  avatarImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/avatar_elena_rodriguez.png",
  companyLogo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/logo_buildcorp.png"
}, {
  id: 4,
  quote: "The brutal efficiency of their fleet operations is exactly what our industrial supply business needed. They handle the heavy lifting so we can focus on sales.",
  name: "David Chen",
  role: "Supply Chain Lead",
  company: "Industrial Edge",
  avatarImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/avatar_david_chen.png",
  companyLogo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634045/img/logo_industrial_edge.png"
}];
export default function VoiceOfTheSupplyChain() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="voice-of-the-supply-chain" className="dark bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`mb-16 md:mb-24 max-w-3xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
            Voice of the Supply Chain
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl font-default">
            Hear from the logistics directors and operations managers who rely on TransitLink to keep their materials moving across the DFW metroplex and the Southeast.
          </p>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full relative">
          <CarouselContent className="-ml-4 md:-ml-8">
            {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pl-4 md:pl-8 md:basis-4/5 lg:basis-2/3">
                <Card className="h-full border-2 border-border bg-card/40 backdrop-blur-md rounded-md p-8 md:p-12 flex flex-col justify-between transition-colors hover:bg-card/60">
                  <div className="space-y-8">
                    <Quote className="size-10 md:size-12 text-primary opacity-80" />
                    <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-relaxed text-foreground">
                      "{testimonial.quote}"
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mt-12 pt-8 border-t-2 border-border/50">
                    <div className="flex items-center gap-4">
                      <SafeHydrate><Avatar className="size-14 md:size-16 border-2 border-border rounded-md">
                        <AvatarImage src={testimonial.avatarImg} alt={testimonial.name} className="object-cover" />
                        <AvatarFallback className="font-default font-bold rounded-md bg-muted text-muted-foreground">
                          {testimonial.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div>
                        <h4 className="font-default font-bold text-lg text-foreground">
                          {testimonial.name}
                        </h4>
                        <p className="text-muted-foreground font-default text-sm">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>

                    <Badge variant="outline" className="border-2 border-border rounded-sm px-3 py-2 gap-3 bg-background/50 backdrop-blur-sm self-start sm:self-auto">
                      <img src={testimonial.companyLogo} alt={`${testimonial.company} logo`} className="h-5 w-auto object-contain grayscale opacity-80" />
                      <span className="font-default font-bold tracking-wider uppercase text-xs text-foreground">
                        {testimonial.company}
                      </span>
                    </Badge>
                  </div>
                </Card>
              </CarouselItem>)}
          </CarouselContent>

          <div className="flex justify-end gap-4 mt-12 pr-4 md:pr-8">
            <CarouselPrevious className="static translate-y-0 translate-x-0 left-auto right-auto top-auto bottom-auto size-14 rounded-md border-2 border-border bg-background hover:bg-primary hover:text-primary-foreground transition-colors" />
            <CarouselNext className="static translate-y-0 translate-x-0 left-auto right-auto top-auto bottom-auto size-14 rounded-md border-2 border-border bg-background hover:bg-primary hover:text-primary-foreground transition-colors" />
          </div>
        </Carousel>
      </div>
    </section>;
}